/* eslint-disable no-param-reassign */
// Dependency-free SVG chart kit for the Metrics screen, ported from the
// approved concept mock (docs/metrics_screen_mock.html). Colors come from
// CSS custom properties defined in stylesheets/metrics.scss so the charts
// stay in sync with the page theme. Chart functions render INTO the host
// element they're given, hence the param-reassign disable.

export const SLOTS = ['var(--m-s1)', 'var(--m-s2)', 'var(--m-s3)', 'var(--m-s4)', 'var(--m-s5)', 'var(--m-s6)']
const OTHER = 'var(--m-other)'
const OTHER_LABELS = ['Other', 'No status', 'No template']

export function seriesColor(label, index) {
  if (OTHER_LABELS.includes(label)) return OTHER
  return SLOTS[index % SLOTS.length]
}

const SVGNS = 'http://www.w3.org/2000/svg'

function sv(tag, attrs, parent) {
  const e = document.createElementNS(SVGNS, tag)
  Object.keys(attrs).forEach(k => e.setAttribute(k, attrs[k]))
  if (parent) parent.appendChild(e)
  return e
}

function div(cls, parent) {
  const e = document.createElement('div')
  if (cls) e.className = cls
  if (parent) parent.appendChild(e)
  return e
}

export function fmtNum(n, dp = 0) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—'
  return Number(n).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function fmtDate(iso) {
  const d = new Date(`${iso}T12:00:00`)
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`
}

function niceStep(rough) {
  const mag = 10 ** Math.floor(Math.log(rough) / Math.LN10)
  const norm = rough / mag
  let step = 10
  if (norm <= 1) step = 1
  else if (norm <= 2) step = 2
  else if (norm <= 2.5) step = 2.5
  else if (norm <= 5) step = 5
  return step * mag
}

function niceTicks(max, n) {
  if (max <= 0) max = 1
  const step = niceStep(max / n)
  const ticks = []
  for (let v = 0; v <= max + step * 0.999; v += step) ticks.push(Math.round(v * 1000) / 1000)
  return ticks
}

// ---------- tooltip (singleton) ----------
let tipEl = null

function tip() {
  if (!tipEl) {
    tipEl = div('metrics-tip')
    document.body.appendChild(tipEl)
  }
  return tipEl
}

function tipMove(ev) {
  const t = tip()
  const pad = 14
  let x = ev.clientX + pad
  let y = ev.clientY + pad
  if (x + t.offsetWidth > window.innerWidth - 8) x = ev.clientX - t.offsetWidth - pad
  if (y + t.offsetHeight > window.innerHeight - 8) y = ev.clientY - t.offsetHeight - pad
  t.style.left = `${x}px`
  t.style.top = `${y}px`
}

function tipShow(build, ev) {
  const t = tip()
  t.innerHTML = ''
  build(t)
  t.style.display = 'block'
  tipMove(ev)
}

export function tipHide() {
  if (tipEl) tipEl.style.display = 'none'
}

function tipTitle(parent, s) {
  div('t-title', parent).textContent = s
}

function tipRow(parent, color, value, label) {
  const r = div('t-row', parent)
  if (color) div('t-key', r).style.background = color
  div('t-val', r).textContent = value
  div('t-lbl', r).textContent = label
  return r
}

function tipHint(parent, s) {
  div('t-hint', parent).textContent = s
}

// ---------- shared pieces ----------
function roundedTopRect(x, y, w, h, r) {
  r = Math.max(0, Math.min(r, w / 2, h))
  return `M${x},${y + h} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} Z`
}

function roundedRightRect(x, y, w, h, r) {
  r = Math.max(0, Math.min(r, h / 2, w))
  return `M${x},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h - r} Q${x + w},${y + h} ${x + w - r},${y + h} L${x},${y + h} Z`
}

function drawGrid(svg, mL, mT, innerW, plotH, ticks, yMax) {
  ticks.forEach((t) => {
    const y = mT + plotH - (t / yMax) * plotH
    sv('line', { x1: mL, x2: mL + innerW, y1: y, y2: y, stroke: t === 0 ? 'var(--m-baseline)' : 'var(--m-grid)', 'stroke-width': 1 }, svg)
    if (t > 0) {
      const lbl = sv('text', { x: mL - 7, y: y + 3.5, 'text-anchor': 'end', 'font-size': 11, fill: 'var(--m-muted)' }, svg)
      lbl.textContent = fmtNum(t)
    }
  })
}

function emptyMsg(host, msg) {
  host.innerHTML = ''
  div('m-empty', host).textContent = msg || 'No data for these filters.'
}

// ---------- charts ----------

// data: { weeks: [iso...], series: [{ label, values: [...] }] }
export function stackedColumns(host, data, opts = {}) {
  host.innerHTML = ''
  const weeks = data.weeks || []
  const series = (data.series || []).map((s, i) => ({
    ...s,
    color: (opts.colorMap && opts.colorMap[s.label]) || seriesColor(s.label, i)
  }))
  const totals = weeks.map((_, i) => series.reduce((a, s) => a + (s.values[i] || 0), 0))
  if (!weeks.length || !totals.some(t => t > 0)) { emptyMsg(host); return null }
  const W = Math.max(host.clientWidth || 640, 320)
  const plotH = opts.plotH || 218
  const mT = 10; const mB = 24; const mL = 42; const mR = 8
  const svg = sv('svg', { width: W, height: mT + plotH + mB }, host)
  const innerW = W - mL - mR
  const ticks = niceTicks(Math.max(...totals), 4)
  const yMax = ticks[ticks.length - 1]
  drawGrid(svg, mL, mT, innerW, plotH, ticks, yMax)
  const band = innerW / weeks.length
  const barW = Math.min(24, band * 0.62)
  const labelEvery = Math.max(1, Math.ceil(weeks.length / Math.floor(innerW / 56)))
  const yPix = v => mT + plotH - (v / yMax) * plotH

  weeks.forEach((week, i) => {
    const cx = mL + band * i + band / 2
    const g = sv('g', {}, svg)
    const segs = series.filter(s => (s.values[i] || 0) > 0)
    let cum = 0
    segs.forEach((s, si) => {
      const v = s.values[i]
      const yTop = yPix(cum + v)
      const yBot = yPix(cum)
      const isTop = si === segs.length - 1
      const gapTop = isTop ? 0 : 2 // 2px surface gap between stacked segments
      const h = Math.max(yBot - yTop - gapTop, 0.75)
      const x = cx - barW / 2
      if (isTop) sv('path', { d: roundedTopRect(x, yTop, barW, h, 4), fill: s.color }, g)
      else sv('rect', { x, y: yTop + gapTop, width: barW, height: h, fill: s.color }, g)
      cum += v
    })
    if (i % labelEvery === 0) {
      const t = sv('text', { x: cx, y: mT + plotH + 16, 'text-anchor': 'middle', 'font-size': 11, fill: 'var(--m-muted)' }, svg)
      t.textContent = fmtDate(week)
    }
    const hit = sv('rect', { x: mL + band * i, y: mT, width: band, height: plotH, fill: 'transparent' }, svg)
    if (opts.onClick) hit.style.cursor = 'pointer'
    hit.addEventListener('pointerenter', () => { g.style.filter = 'brightness(1.08)' })
    hit.addEventListener('pointerleave', () => { g.style.filter = ''; tipHide() })
    hit.addEventListener('pointermove', (ev) => {
      tipShow((p) => {
        tipTitle(p, `Week of ${fmtDate(week)}`);
        [...series].reverse().forEach((s) => {
          if ((s.values[i] || 0) > 0) tipRow(p, s.color, fmtNum(s.values[i]), s.label)
        })
        if (series.length > 1) tipRow(p, null, fmtNum(totals[i]), 'Total').classList.add('t-total')
        if (opts.clickHint) div('t-hint', p).textContent = opts.clickHint
      }, ev)
    })
    if (opts.onClick) hit.addEventListener('click', () => opts.onClick(week))
  })
  return series
}

// rows: [{ label, value, tipExtra, color }]
export function hBars(host, rows, opts = {}) {
  host.innerHTML = ''
  if (!rows.length || !rows.some(r => r.value > 0)) { emptyMsg(host); return }
  const W = Math.max(host.clientWidth || 480, 300)
  const rowH = 32; const mT = 6
  const labelW = opts.labelW || 150; const valueW = 64; const mR = 10
  const svg = sv('svg', { width: W, height: mT + rows.length * rowH + 4 }, host)
  const innerW = W - labelW - valueW - mR
  const maxV = Math.max(...rows.map(r => r.value))
  rows.forEach((r, i) => {
    const yMid = mT + rowH * i + rowH / 2
    const lbl = sv('text', { x: labelW - 10, y: yMid + 4, 'text-anchor': 'end', 'font-size': 12, fill: 'var(--m-ink2)' }, svg)
    lbl.textContent = r.label
    const w = Math.max((r.value / maxV) * innerW, 1.5)
    sv('path', { d: roundedRightRect(labelW, yMid - 9, w, 18, 4), fill: r.color || opts.color || 'var(--m-s1)' }, svg)
    const val = sv('text', { x: labelW + w + 8, y: yMid + 4, 'font-size': 12, fill: 'var(--m-ink)', 'font-weight': 600 }, svg)
    val.textContent = opts.fmt ? opts.fmt(r.value) : fmtNum(r.value)
    const hit = sv('rect', { x: 0, y: mT + rowH * i, width: W, height: rowH, fill: 'transparent' }, svg)
    hit.addEventListener('pointermove', (ev) => {
      tipShow((p) => {
        tipTitle(p, r.label)
        tipRow(p, r.color || opts.color || 'var(--m-s1)', opts.fmt ? opts.fmt(r.value) : fmtNum(r.value), opts.unitLabel || '')
        if (r.tipExtra) tipRow(p, null, r.tipExtra, '')
        if (opts.clickHint) div('t-hint', p).textContent = opts.clickHint
      }, ev)
    })
    hit.addEventListener('pointerleave', tipHide)
    if (opts.onClick) {
      hit.style.cursor = 'pointer'
      hit.addEventListener('click', () => opts.onClick(r))
    }
  })
}

// points: [{ x: iso, y: number, n }] — single series line with crosshair
export function lineChart(host, points, opts = {}) {
  host.innerHTML = ''
  const pts = points.filter(p => p.y !== null && !Number.isNaN(p.y))
  if (pts.length < 2) { emptyMsg(host, 'Not enough data for these filters.'); return }
  const W = Math.max(host.clientWidth || 480, 300)
  const plotH = opts.plotH || 190
  const mT = 12; const mB = 24; const mL = 42; const mR = 52
  const svg = sv('svg', { width: W, height: mT + plotH + mB }, host)
  const innerW = W - mL - mR
  const ticks = niceTicks(Math.max(...pts.map(p => p.y)), 4)
  const yMax = ticks[ticks.length - 1]
  drawGrid(svg, mL, mT, innerW, plotH, ticks, yMax)
  const xs = pts.map(p => new Date(`${p.x}T12:00:00`).getTime())
  const x0 = xs[0]; const x1 = xs[xs.length - 1]
  const X = t => mL + (x1 === x0 ? 0.5 : (t - x0) / (x1 - x0)) * innerW
  const Y = v => mT + plotH - (v / yMax) * plotH
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${X(xs[i]).toFixed(1)},${Y(p.y).toFixed(1)}`).join(' ')
  sv('path', { d, fill: 'none', stroke: 'var(--m-s1)', 'stroke-width': 2, 'stroke-linejoin': 'round', 'stroke-linecap': 'round' }, svg)
  const nx = Math.max(2, Math.floor(innerW / 90))
  for (let i = 0; i < nx; i += 1) {
    const t = x0 + (x1 - x0) * (i / (nx - 1))
    let anchor = 'middle'
    if (i === 0) anchor = 'start'
    if (i === nx - 1) anchor = 'end'
    const xl = sv('text', { x: X(t), y: mT + plotH + 16, 'text-anchor': anchor, 'font-size': 11, fill: 'var(--m-muted)' }, svg)
    xl.textContent = fmtDate(new Date(t).toISOString().slice(0, 10))
  }
  const last = pts[pts.length - 1]
  sv('circle', { cx: X(xs[xs.length - 1]), cy: Y(last.y), r: 4.5, fill: 'var(--m-s1)', stroke: 'var(--m-surface)', 'stroke-width': 2 }, svg)
  const endLbl = sv('text', { x: X(xs[xs.length - 1]) + 9, y: Y(last.y) + 4, 'font-size': 12, 'font-weight': 650, fill: 'var(--m-ink)' }, svg)
  endLbl.textContent = opts.fmt ? opts.fmt(last.y) : fmtNum(last.y)
  const cross = sv('line', { y1: mT, y2: mT + plotH, stroke: 'var(--m-baseline)', 'stroke-width': 1, opacity: 0 }, svg)
  const dot = sv('circle', { r: 4.5, fill: 'var(--m-s1)', stroke: 'var(--m-surface)', 'stroke-width': 2, opacity: 0 }, svg)
  const hit = sv('rect', { x: mL, y: mT, width: innerW, height: plotH, fill: 'transparent' }, svg)
  hit.addEventListener('pointermove', (ev) => {
    const rect = svg.getBoundingClientRect()
    const px = ev.clientX - rect.left
    let best = 0; let bd = Infinity
    xs.forEach((x, i) => { const dd = Math.abs(X(x) - px); if (dd < bd) { bd = dd; best = i } })
    cross.setAttribute('x1', X(xs[best]))
    cross.setAttribute('x2', X(xs[best]))
    cross.setAttribute('opacity', 1)
    dot.setAttribute('cx', X(xs[best]))
    dot.setAttribute('cy', Y(pts[best].y))
    dot.setAttribute('opacity', 1)
    tipShow((p) => {
      tipTitle(p, `Week of ${fmtDate(pts[best].x)}`)
      tipRow(p, 'var(--m-s1)', opts.fmt ? opts.fmt(pts[best].y) : fmtNum(pts[best].y), opts.seriesLabel || '')
      if (pts[best].n) tipRow(p, null, fmtNum(pts[best].n), opts.nLabel || '')
    }, ev)
  })
  hit.addEventListener('pointerleave', () => {
    cross.setAttribute('opacity', 0)
    dot.setAttribute('opacity', 0)
    tipHide()
  })
}

export function sparkline(host, values) {
  host.innerHTML = ''
  if (!values || values.length < 2) return
  const W = 104; const H = 28; const p = 3
  const svg = sv('svg', { width: W, height: H }, host)
  const lo = Math.min(...values); const hi = Math.max(...values)
  const span = (hi - lo) || 1
  const X = i => p + (i / (values.length - 1)) * (W - 2 * p)
  const Y = v => H - p - ((v - lo) / span) * (H - 2 * p)
  const d = values.map((v, i) => `${i ? 'L' : 'M'}${X(i).toFixed(1)},${Y(v).toFixed(1)}`).join(' ')
  sv('path', { d, fill: 'none', stroke: 'var(--m-baseline)', 'stroke-width': 1.5, 'stroke-linejoin': 'round' }, svg)
  sv('circle', { cx: X(values.length - 1), cy: Y(values[values.length - 1]), r: 2.8, fill: 'var(--m-s1)' }, svg)
}

// Accessible twin: cols [{label, num}], rows [[...]]
export function renderTable(host, cols, rows, onRowClick) {
  host.innerHTML = ''
  const table = document.createElement('table')
  table.className = 'm-table'
  const thead = document.createElement('thead')
  const trh = document.createElement('tr')
  cols.forEach((c) => {
    const th = document.createElement('th')
    th.textContent = c.label
    if (c.num) th.className = 'num'
    trh.appendChild(th)
  })
  thead.appendChild(trh)
  table.appendChild(thead)
  const tbody = document.createElement('tbody')
  rows.forEach((r, ri) => {
    const tr = document.createElement('tr')
    r.forEach((cell, i) => {
      const td = document.createElement('td')
      td.textContent = cell
      if (cols[i].num) td.className = 'num'
      tr.appendChild(td)
    })
    if (onRowClick) {
      tr.className = 'm-rowlink'
      tr.addEventListener('click', () => onRowClick(ri))
    }
    tbody.appendChild(tr)
  })
  table.appendChild(tbody)
  host.appendChild(table)
}

// KPI tile: { label, value, delta, deltaText, upIsGood, sub, spark, dot }
export function kpiTile(host, o) {
  host.innerHTML = ''
  const label = div('m-kpi-label', host)
  if (o.dot) {
    const kd = document.createElement('span')
    kd.className = 'm-kdot'
    kd.style.background = o.dot
    label.appendChild(kd)
  }
  label.appendChild(document.createTextNode(o.label))
  const row = div('m-kpi-row', host)
  div('m-kpi-value', row).textContent = o.value
  if (o.delta !== null && o.delta !== undefined && o.deltaText) {
    const delta = div('m-kpi-delta', row)
    const upGood = o.upIsGood !== false
    if (o.delta === 0) delta.classList.add('flat')
    else delta.classList.add((o.delta > 0) === upGood ? 'good' : 'bad')
    let arrow = '· '
    if (o.delta > 0) arrow = '▲ '
    if (o.delta < 0) arrow = '▼ '
    delta.textContent = `${arrow}${o.deltaText} vs prior period`
  }
  if (o.sub) div('m-kpi-sub', host).textContent = o.sub
  const spark = div('m-kpi-spark', host)
  if (o.spark && o.spark.length > 1) sparkline(spark, o.spark.slice(-12))
}

// ---------- Trends: control chart ----------
// points: [{ at: iso, value, status, job }]; refLines: [{ y, label, kind }]
export function controlChart(host, points, opts = {}) {
  host.innerHTML = ''
  if (points.length < 2) { emptyMsg(host, 'Not enough data for these filters.'); return }
  const W = Math.max(host.clientWidth || 640, 320)
  const plotH = opts.plotH || 240
  const mT = 14; const mB = 24; const mL = 48; const mR = 64
  const svg = sv('svg', { width: W, height: mT + plotH + mB }, host)
  const innerW = W - mL - mR
  const vals = points.map(p => p.value)
  const refVals = (opts.refLines || []).map(r => r.y)
  let lo = Math.min(...vals, ...(refVals.length ? refVals : [Infinity]))
  let hi = Math.max(...vals, ...(refVals.length ? refVals : [-Infinity]))
  const pad = (hi - lo) * 0.12 || 1
  lo -= pad; hi += pad
  const xs = points.map(p => new Date(p.at).getTime())
  const x0 = xs[0]; const x1 = xs[xs.length - 1]
  const X = t => mL + (x1 === x0 ? 0.5 : (t - x0) / (x1 - x0)) * innerW
  const Y = v => mT + plotH - ((v - lo) / (hi - lo)) * plotH
  const step = (hi - lo) / 4
  for (let gv = lo + step; gv < hi; gv += step) {
    sv('line', { x1: mL, x2: mL + innerW, y1: Y(gv), y2: Y(gv), stroke: 'var(--m-grid)', 'stroke-width': 1 }, svg)
    const gl = sv('text', { x: mL - 7, y: Y(gv) + 3.5, 'text-anchor': 'end', 'font-size': 11, fill: 'var(--m-muted)' }, svg)
    gl.textContent = fmtNum(gv, opts.dp || 0)
  }
  sv('line', { x1: mL, x2: mL + innerW, y1: mT + plotH, y2: mT + plotH, stroke: 'var(--m-baseline)', 'stroke-width': 1 }, svg)
  const nx = Math.max(2, Math.floor(innerW / 90))
  for (let i = 0; i < nx; i += 1) {
    const t = x0 + (x1 - x0) * (i / (nx - 1))
    let anchor = 'middle'
    if (i === 0) anchor = 'start'
    if (i === nx - 1) anchor = 'end'
    const xl = sv('text', { x: X(t), y: mT + plotH + 16, 'text-anchor': anchor, 'font-size': 11, fill: 'var(--m-muted)' }, svg)
    xl.textContent = fmtDate(new Date(t).toISOString().slice(0, 10))
  }
  (opts.refLines || []).forEach((r) => {
    const spec = r.kind === 'criteria'
    sv('line', {
      x1: mL,
      x2: mL + innerW,
      y1: Y(r.y),
      y2: Y(r.y),
      stroke: spec ? 'var(--m-st-fail)' : 'var(--m-baseline)',
      'stroke-width': 1,
      'stroke-dasharray': spec ? '5 4' : 'none',
      opacity: spec ? 0.55 : 1
    }, svg)
    const rl = sv('text', { x: mL + innerW + 6, y: Y(r.y) + 3.5, 'font-size': 11, 'font-weight': 600, fill: spec ? 'var(--m-bad)' : 'var(--m-muted)' }, svg)
    rl.textContent = r.label
  })
  if (!opts.noLine) {
    const d = points.map((p, i) => `${i ? 'L' : 'M'}${X(xs[i]).toFixed(1)},${Y(p.value).toFixed(1)}`).join(' ')
    sv('path', { d, fill: 'none', stroke: 'var(--m-s1)', 'stroke-width': 1.5, opacity: 0.45, 'stroke-linejoin': 'round' }, svg)
  }
  const pointColor = (p) => {
    if (p.status === 'fail') return 'var(--m-st-fail)'
    if (p.status === 'pass') return 'var(--m-s1)'
    return 'var(--m-st-nt)'
  }
  points.forEach((p, i) => {
    sv('circle', { cx: X(xs[i]), cy: Y(p.value), r: 4.5, fill: pointColor(p), stroke: 'var(--m-surface)', 'stroke-width': 2 }, svg)
    const hit = sv('circle', { cx: X(xs[i]), cy: Y(p.value), r: 13, fill: 'transparent' }, svg)
    hit.addEventListener('pointermove', (ev) => {
      tipShow((el) => {
        tipTitle(el, new Date(p.at).toLocaleDateString())
        tipRow(el, pointColor(p), `${fmtNum(p.value, opts.dp || 0)} ${opts.unit || ''}`, p.status)
        tipRow(el, null, p.job || '', '')
      }, ev)
    })
    hit.addEventListener('pointerleave', tipHide)
  })
}

// ---------- Trends: field strip ----------
// One horizontal dot strip: each entry is one sample's average for a field
// ({ sample, job, material, avg, n, fail, outlier }). Criteria zones shade
// the fail side(s) when opts.criteria = { min, max } is present. Fail dots
// are red (stored status), low-N dots (< 3 results) go pale, outliers are
// diamonds. Same visual grammar as the Results Summary card's strips.
export function fieldStrip(host, entries, opts = {}) {
  host.innerHTML = ''
  if (!entries.length) { emptyMsg(host, 'No samples in range.'); return }
  const W = Math.max(host.clientWidth || 480, 260)
  const bandH = 32; const mT = 4; const mB = 15; const mL = 8; const mR = 8
  const svg = sv('svg', { width: W, height: mT + bandH + mB }, host)
  const innerW = W - mL - mR
  const dp = opts.dp || 0
  let lo = Math.min(...entries.map(e => e.avg))
  let hi = Math.max(...entries.map(e => e.avg))
  const c = opts.criteria
  if (c) {
    if (c.min !== null && c.min !== undefined) lo = Math.min(lo, c.min)
    if (c.max !== null && c.max !== undefined) hi = Math.max(hi, c.max)
  }
  const pad = (hi - lo) * 0.08 || 1
  lo -= pad; hi += pad
  const X = v => mL + ((v - lo) / (hi - lo)) * innerW
  if (c) {
    [{ v: c.min, lbl: 'Min', left: true }, { v: c.max, lbl: 'Max', left: false }].forEach((s) => {
      if (s.v === null || s.v === undefined) return
      const x = X(s.v)
      sv('rect', {
        x: s.left ? mL : x,
        y: mT,
        width: Math.max(0, s.left ? x - mL : mL + innerW - x),
        height: bandH,
        fill: 'var(--m-st-fail)',
        opacity: 0.07
      }, svg)
      sv('line', { x1: x, x2: x, y1: mT, y2: mT + bandH, stroke: 'var(--m-st-fail)', 'stroke-width': 1, 'stroke-dasharray': '4 3', opacity: 0.5 }, svg)
      const lb = sv('text', { x, y: mT + bandH + 11, 'text-anchor': 'middle', 'font-size': 10.5, 'font-weight': 600, fill: 'var(--m-bad)' }, svg)
      lb.textContent = `${s.lbl} ${fmtNum(s.v, dp)}`
      lb.style.fontVariantNumeric = 'tabular-nums'
    })
  }
  const nearCriteria = (x) => {
    if (!c) return false
    return [c.min, c.max].some(v => v !== null && v !== undefined && Math.abs(x - X(v)) < 46)
  }
  const endLabels = [{ v: lo + pad, anchor: 'start', x: mL }, { v: hi - pad, anchor: 'end', x: mL + innerW }]
  endLabels.forEach((t) => {
    if (nearCriteria(t.x)) return
    const tl = sv('text', { x: t.x, y: mT + bandH + 11, 'text-anchor': t.anchor, 'font-size': 10.5, fill: 'var(--m-muted)' }, svg)
    tl.textContent = fmtNum(t.v, dp)
    tl.style.fontVariantNumeric = 'tabular-nums'
  })
  const cy = mT + bandH / 2
  sv('line', { x1: mL, x2: mL + innerW, y1: cy, y2: cy, stroke: 'var(--m-grid)', 'stroke-width': 1 }, svg)
  // deterministic jitter keyed on the sample so re-renders don't reshuffle
  const jitter = (s) => {
    let h = 0
    for (let i = 0; i < s.length; i += 1) h = ((h * 31) + s.charCodeAt(i)) % 1000003
    return ((h % 1000) / 1000) - 0.5
  }
  const restingFill = opts.judged ? 'var(--m-s1)' : 'var(--m-st-nt)'
  entries.forEach((e) => {
    const cx = X(e.avg)
    const y = cy + (jitter(`${e.job}·${e.sample}`) * (bandH - 12))
    const fill = e.fail ? 'var(--m-st-fail)' : restingFill
    let mark
    if (e.outlier) {
      const r = 4.8
      mark = sv('path', {
        d: `M${cx} ${y - r} L${cx + r} ${y} L${cx} ${y + r} L${cx - r} ${y} Z`,
        fill,
        stroke: 'var(--m-surface)',
        'stroke-width': 1.5
      }, svg)
    } else {
      mark = sv('circle', { cx, cy: y, r: 4, fill, stroke: 'var(--m-surface)', 'stroke-width': 1.5 }, svg)
    }
    if (e.n < 3 && !e.fail) mark.setAttribute('opacity', 0.4)
    const hit = sv('circle', { cx, cy: y, r: 9, fill: 'transparent' }, svg)
    if (opts.onOpen) hit.style.cursor = 'pointer'
    hit.addEventListener('pointermove', (ev) => {
      let verdict = 'average'
      if (opts.judged) verdict = e.fail ? 'fail' : 'pass'
      tipShow((el) => {
        tipTitle(el, `${e.job} · ${e.sample || 'sample'}`)
        tipRow(el, fill, `${fmtNum(e.avg, dp)}`, `${verdict} · ${e.n} result${e.n === 1 ? '' : 's'}`)
        if (e.material) tipRow(el, null, e.material, '')
        if (e.outlier) tipRow(el, null, 'Statistical outlier', 'modified z ≥ 3.5')
        if (e.n < 3) tipRow(el, null, 'Low evidence', 'under 3 results')
        if (opts.onOpen) tipHint(el, "Open this job's tasks screen")
      }, ev)
    })
    hit.addEventListener('pointerleave', tipHide)
    if (opts.onOpen) {
      hit.addEventListener('click', (ev) => {
        ev.stopPropagation()
        opts.onOpen(e)
      })
    }
  })
}

// ---------- Trends: weekly whiskers (high-volume mode) ----------
// weeks: [{ week: iso, n, min, q1, med, q3, max, fails }] — bucketed in SQL.
// Median tick + middle-50% box + min–max whisker per week; a red dot under
// a week means it contains failing values.
export function weeklyWhiskers(host, weeks, opts = {}) {
  host.innerHTML = ''
  const present = weeks.filter(w => w.n > 0)
  if (present.length < 2) { emptyMsg(host, 'Not enough data for these filters.'); return }
  const W = Math.max(host.clientWidth || 640, 320)
  const plotH = opts.plotH || 240
  const mT = 14; const mB = 24; const mL = 48; const mR = 64
  const svg = sv('svg', { width: W, height: mT + plotH + mB }, host)
  const innerW = W - mL - mR
  const refVals = (opts.refLines || []).map(r => r.y)
  let lo = Math.min(...present.map(w => w.min), ...(refVals.length ? refVals : [Infinity]))
  let hi = Math.max(...present.map(w => w.max), ...(refVals.length ? refVals : [-Infinity]))
  const pad = (hi - lo) * 0.1 || 1
  lo -= pad; hi += pad
  const Y = v => mT + plotH - ((v - lo) / (hi - lo)) * plotH
  const band = innerW / weeks.length
  const step = (hi - lo) / 4
  for (let gv = lo + step; gv < hi; gv += step) {
    sv('line', { x1: mL, x2: mL + innerW, y1: Y(gv), y2: Y(gv), stroke: 'var(--m-grid)', 'stroke-width': 1 }, svg)
    const gl = sv('text', { x: mL - 7, y: Y(gv) + 3.5, 'text-anchor': 'end', 'font-size': 11, fill: 'var(--m-muted)' }, svg)
    gl.textContent = fmtNum(gv, opts.dp || 0)
  }
  sv('line', { x1: mL, x2: mL + innerW, y1: mT + plotH, y2: mT + plotH, stroke: 'var(--m-baseline)', 'stroke-width': 1 }, svg)
  const nx = Math.max(2, Math.floor(innerW / 90))
  for (let i = 0; i < nx; i += 1) {
    const wi = Math.round((weeks.length - 1) * (i / (nx - 1)))
    let anchor = 'middle'
    if (i === 0) anchor = 'start'
    if (i === nx - 1) anchor = 'end'
    const xl = sv('text', { x: mL + ((wi + 0.5) * band), y: mT + plotH + 16, 'text-anchor': anchor, 'font-size': 11, fill: 'var(--m-muted)' }, svg)
    xl.textContent = fmtDate(weeks[wi].week)
  }
  const refs = opts.refLines || []
  refs.forEach((r) => {
    const isCriteria = r.kind === 'criteria'
    sv('line', {
      x1: mL,
      x2: mL + innerW,
      y1: Y(r.y),
      y2: Y(r.y),
      stroke: isCriteria ? 'var(--m-st-fail)' : 'var(--m-baseline)',
      'stroke-width': 1,
      'stroke-dasharray': isCriteria ? '5 4' : 'none',
      opacity: isCriteria ? 0.55 : 1
    }, svg)
    const rl = sv('text', { x: mL + innerW + 6, y: Y(r.y) + 3.5, 'font-size': 11, 'font-weight': 600, fill: isCriteria ? 'var(--m-bad)' : 'var(--m-muted)' }, svg)
    rl.textContent = r.label
  })
  weeks.forEach((s, i) => {
    if (!s.n) return
    const x = mL + ((i + 0.5) * band)
    const bw = Math.min(18, band * 0.55)
    sv('line', { x1: x, x2: x, y1: Y(s.max), y2: Y(s.min), stroke: 'var(--m-baseline)', 'stroke-width': 1.2 }, svg)
    sv('rect', { x: x - (bw / 2), y: Y(s.q3), width: bw, height: Math.max(2, Y(s.q1) - Y(s.q3)), rx: 2, fill: 'var(--m-s1)', opacity: 0.4 }, svg)
    sv('line', { x1: x - (bw / 2), x2: x + (bw / 2), y1: Y(s.med), y2: Y(s.med), stroke: 'var(--m-s1)', 'stroke-width': 2.5 }, svg)
    if (s.fails) sv('circle', { cx: x, cy: mT + plotH - 5, r: 3, fill: 'var(--m-st-fail)' }, svg)
    const hit = sv('rect', { x: mL + (i * band), y: mT, width: band, height: plotH, fill: 'transparent' }, svg)
    hit.addEventListener('pointermove', (ev) => {
      tipShow((el) => {
        tipTitle(el, `Week of ${fmtDate(s.week)}`)
        tipRow(el, 'var(--m-s1)', `${fmtNum(s.med, opts.dp || 0)} ${opts.unit || ''}`, 'median')
        tipRow(el, null, `${fmtNum(s.q1, opts.dp || 0)} – ${fmtNum(s.q3, opts.dp || 0)}`, 'middle 50%')
        tipRow(el, null, `${fmtNum(s.min, opts.dp || 0)} – ${fmtNum(s.max, opts.dp || 0)}`, 'full range')
        tipRow(el, s.fails ? 'var(--m-st-fail)' : null, fmtNum(s.n), `results${s.fails ? ` · ${s.fails} fail` : ''}`)
      }, ev)
    })
    hit.addEventListener('pointerleave', tipHide)
  })
}

// ---------- Trends: histogram from server-side bins ----------
// bins: { lo, hi, counts } — weekly mode has no raw points to bin client-side.
export function binsHistogram(host, bins, opts = {}) {
  host.innerHTML = ''
  const counts = bins.counts || []
  if (!counts.some(c => c > 0)) { emptyMsg(host, 'Not enough data for these filters.'); return }
  const W = Math.max(host.clientWidth || 480, 300)
  const plotH = 170; const mT = 10; const mB = 26; const mL = 34; const mR = 10
  let { lo, hi } = bins
  if (opts.min !== null && opts.min !== undefined) lo = Math.min(lo, opts.min)
  if (opts.max !== null && opts.max !== undefined) hi = Math.max(hi, opts.max)
  const span = (hi - lo) || 1
  const dLo = lo - (span * 0.06); const dHi = hi + (span * 0.06)
  const svg = sv('svg', { width: W, height: mT + plotH + mB }, host)
  const innerW = W - mL - mR
  const ticks = niceTicks(Math.max(...counts), 3)
  const yMax = ticks[ticks.length - 1]
  drawGrid(svg, mL, mT, innerW, plotH, ticks, yMax)
  const X = v => mL + ((v - dLo) / (dHi - dLo)) * innerW
  const bw = (bins.hi - bins.lo) / counts.length
  counts.forEach((c, i) => {
    if (!c) return
    const x0 = X(bins.lo + (bw * i)); const x1 = X(bins.lo + (bw * (i + 1)))
    const h = (c / yMax) * plotH
    sv('path', { d: roundedTopRect(x0 + 1, mT + plotH - h, Math.max(1, x1 - x0 - 2), h, 3), fill: 'var(--m-s1)' }, svg)
    const hit = sv('rect', { x: x0, y: mT, width: Math.max(1, x1 - x0), height: plotH, fill: 'transparent' }, svg)
    hit.addEventListener('pointermove', (ev) => {
      tipShow((el) => {
        tipTitle(el, `${fmtNum(bins.lo + (bw * i), opts.dp || 0)} – ${fmtNum(bins.lo + (bw * (i + 1)), opts.dp || 0)} ${opts.unit || ''}`)
        tipRow(el, 'var(--m-s1)', fmtNum(c), 'results')
      }, ev)
    })
    hit.addEventListener('pointerleave', tipHide)
  });
  [{ v: opts.min, lbl: 'Min' }, { v: opts.max, lbl: 'Max' }].forEach((s) => {
    if (s.v === null || s.v === undefined) return
    sv('line', { x1: X(s.v), x2: X(s.v), y1: mT, y2: mT + plotH, stroke: 'var(--m-st-fail)', 'stroke-width': 1, 'stroke-dasharray': '5 4', opacity: 0.55 }, svg)
    const sl = sv('text', { x: X(s.v), y: mT + plotH + 16, 'text-anchor': 'middle', 'font-size': 11, 'font-weight': 600, fill: 'var(--m-bad)' }, svg)
    sl.textContent = `${s.lbl} ${fmtNum(s.v, opts.dp || 0)}`
  })
}

// ---------- Trends: histogram ----------
export function histogram(host, values, opts = {}) {
  host.innerHTML = ''
  if (values.length < 2) { emptyMsg(host, 'Not enough data for these filters.'); return }
  const W = Math.max(host.clientWidth || 480, 300)
  const plotH = 170; const mT = 10; const mB = 26; const mL = 34; const mR = 10
  let lo = Math.min(...values); let hi = Math.max(...values)
  if (opts.min !== null && opts.min !== undefined) lo = Math.min(lo, opts.min)
  if (opts.max !== null && opts.max !== undefined) hi = Math.max(hi, opts.max)
  const span = (hi - lo) || 1
  lo -= span * 0.06; hi += span * 0.06
  const nb = 12
  const bw = (hi - lo) / nb
  const bins = Array(nb).fill(0)
  values.forEach((v) => { bins[Math.min(nb - 1, Math.max(0, Math.floor((v - lo) / bw)))] += 1 })
  const svg = sv('svg', { width: W, height: mT + plotH + mB }, host)
  const innerW = W - mL - mR
  const ticks = niceTicks(Math.max(...bins), 3)
  const yMax = ticks[ticks.length - 1]
  drawGrid(svg, mL, mT, innerW, plotH, ticks, yMax)
  const band = innerW / nb
  const X = v => mL + ((v - lo) / (hi - lo)) * innerW
  bins.forEach((c, i) => {
    if (!c) return
    const h = (c / yMax) * plotH
    sv('path', { d: roundedTopRect(mL + band * i + 1, mT + plotH - h, band - 2, h, 3), fill: 'var(--m-s1)' }, svg)
    const hit = sv('rect', { x: mL + band * i, y: mT, width: band, height: plotH, fill: 'transparent' }, svg)
    hit.addEventListener('pointermove', (ev) => {
      tipShow((el) => {
        tipTitle(el, `${fmtNum(lo + bw * i, opts.dp || 0)} – ${fmtNum(lo + bw * (i + 1), opts.dp || 0)} ${opts.unit || ''}`)
        tipRow(el, 'var(--m-s1)', fmtNum(c), 'results')
      }, ev)
    })
    hit.addEventListener('pointerleave', tipHide)
  });
  [{ v: opts.min, lbl: 'Min' }, { v: opts.max, lbl: 'Max' }].forEach((s) => {
    if (s.v === null || s.v === undefined) return
    sv('line', { x1: X(s.v), x2: X(s.v), y1: mT, y2: mT + plotH, stroke: 'var(--m-st-fail)', 'stroke-width': 1, 'stroke-dasharray': '5 4', opacity: 0.55 }, svg)
    const sl = sv('text', { x: X(s.v), y: mT + plotH + 16, 'text-anchor': 'middle', 'font-size': 11, 'font-weight': 600, fill: 'var(--m-bad)' }, svg)
    sl.textContent = `${s.lbl} ${fmtNum(s.v, opts.dp || 0)}`
  })
  // clean value ticks along the x axis, skipping ones that would collide
  // with the Min/Max criteria labels
  const tickDp = (hi - lo) < 5 ? 1 : 0
  const collides = x => [opts.min, opts.max].some(m => m !== null && m !== undefined && Math.abs(x - X(m)) < 42)
  niceTicks(hi - lo, 4).forEach((off) => {
    const v = lo + off
    if (v > hi || collides(X(v))) return
    const xl = sv('text', { x: X(v), y: mT + plotH + 16, 'text-anchor': 'middle', 'font-size': 11, fill: 'var(--m-muted)' }, svg)
    xl.textContent = fmtNum(v, tickDp)
  })
}

// ---------- Compare: clustered columns ----------
// data: { weeks: [iso], groups: [{ label, color, values: [], tips: [] }] }
export function groupedColumns(host, data, opts = {}) {
  host.innerHTML = ''
  const { weeks, groups } = data
  if (!weeks.length || !groups.some(g => g.values.some(v => v > 0))) { emptyMsg(host); return }
  const W = Math.max(host.clientWidth || 640, 320)
  const plotH = opts.plotH || 218
  const mT = 10; const mB = 24; const mL = 42; const mR = 8
  const svg = sv('svg', { width: W, height: mT + plotH + mB }, host)
  const innerW = W - mL - mR
  const maxV = Math.max(...groups.flatMap(g => g.values))
  const ticks = niceTicks(maxV, 4)
  const yMax = ticks[ticks.length - 1]
  drawGrid(svg, mL, mT, innerW, plotH, ticks, yMax)
  const band = innerW / weeks.length
  const n = groups.length
  const barW = Math.max(3, Math.min(24, (band * 0.72 - 2 * (n - 1)) / n))
  const clusterW = n * barW + 2 * (n - 1)
  const labelEvery = Math.max(1, Math.ceil(weeks.length / Math.floor(innerW / 56)))
  const yPix = v => mT + plotH - (v / yMax) * plotH
  weeks.forEach((week, i) => {
    const g0 = sv('g', {}, svg)
    const cx = mL + band * i + band / 2
    groups.forEach((gr, gi) => {
      const v = gr.values[i] || 0
      if (v <= 0) return
      const x = cx - clusterW / 2 + gi * (barW + 2)
      sv('path', { d: roundedTopRect(x, yPix(v), barW, mT + plotH - yPix(v), 4), fill: gr.color }, g0)
    })
    if (i % labelEvery === 0) {
      const t = sv('text', { x: cx, y: mT + plotH + 16, 'text-anchor': 'middle', 'font-size': 11, fill: 'var(--m-muted)' }, svg)
      t.textContent = fmtDate(week)
    }
    const hit = sv('rect', { x: mL + band * i, y: mT, width: band, height: plotH, fill: 'transparent' }, svg)
    hit.addEventListener('pointerenter', () => { g0.style.filter = 'brightness(1.08)' })
    hit.addEventListener('pointerleave', () => { g0.style.filter = ''; tipHide() })
    hit.addEventListener('pointermove', (ev) => {
      tipShow((p) => {
        tipTitle(p, `Week of ${fmtDate(week)}`)
        groups.forEach((gr) => {
          tipRow(p, gr.color, fmtNum(gr.values[i] || 0), (gr.tips && gr.tips[i]) || gr.label)
        })
      }, ev)
    })
  })
}

// ---------- Compare: multi-series line (weeks-aligned) ----------
// data: { weeks: [iso], series: [{ label, color, points: [y|null] }] }
export function multiLine(host, data, opts = {}) {
  host.innerHTML = ''
  const { weeks, series } = data
  const drawable = series.filter(s => s.points.filter(y => y !== null).length >= 2)
  if (!drawable.length || weeks.length < 2) { emptyMsg(host, 'Not enough data for these filters.'); return }
  const W = Math.max(host.clientWidth || 480, 300)
  const plotH = opts.plotH || 190
  const mT = 12; const mB = 24; const mL = 44; const mR = 14
  const svg = sv('svg', { width: W, height: mT + plotH + mB }, host)
  const innerW = W - mL - mR
  const isPct = opts.percent !== false
  let ticks; let yMax
  if (isPct) { ticks = [0, 25, 50, 75, 100]; yMax = 100 } else {
    const maxY = Math.max(...series.flatMap(s => s.points.filter(y => y !== null)))
    ticks = niceTicks(maxY, 4); yMax = ticks[ticks.length - 1]
  }
  drawGrid(svg, mL, mT, innerW, plotH, ticks, yMax)
  const xs = weeks.map(w => new Date(`${w}T12:00:00`).getTime())
  const x0 = xs[0]; const x1 = xs[xs.length - 1]
  const X = t => mL + (x1 === x0 ? 0.5 : (t - x0) / (x1 - x0)) * innerW
  const Y = v => mT + plotH - (v / yMax) * plotH
  const vFmt = opts.fmt || (v => (isPct ? `${v}%` : fmtNum(v)))
  drawable.forEach((s) => {
    let d = ''
    let started = false
    s.points.forEach((y, i) => {
      if (y === null) return
      d += `${started ? 'L' : 'M'}${X(xs[i]).toFixed(1)},${Y(y).toFixed(1)} `
      started = true
    })
    sv('path', { d: d.trim(), fill: 'none', stroke: s.color, 'stroke-width': 2, 'stroke-linejoin': 'round', 'stroke-linecap': 'round' }, svg)
    for (let i = s.points.length - 1; i >= 0; i -= 1) {
      if (s.points[i] !== null) {
        sv('circle', { cx: X(xs[i]), cy: Y(s.points[i]), r: 4.5, fill: s.color, stroke: 'var(--m-surface)', 'stroke-width': 2 }, svg)
        break
      }
    }
  })
  const nx = Math.max(2, Math.floor(innerW / 90))
  for (let i = 0; i < nx; i += 1) {
    const t = x0 + (x1 - x0) * (i / (nx - 1))
    let anchor = 'middle'
    if (i === 0) anchor = 'start'
    if (i === nx - 1) anchor = 'end'
    const xl = sv('text', { x: X(t), y: mT + plotH + 16, 'text-anchor': anchor, 'font-size': 11, fill: 'var(--m-muted)' }, svg)
    xl.textContent = fmtDate(new Date(t).toISOString().slice(0, 10))
  }
  const cross = sv('line', { y1: mT, y2: mT + plotH, stroke: 'var(--m-baseline)', 'stroke-width': 1, opacity: 0 }, svg)
  const hit = sv('rect', { x: mL, y: mT, width: innerW, height: plotH, fill: 'transparent' }, svg)
  hit.addEventListener('pointermove', (ev) => {
    const rect = svg.getBoundingClientRect()
    const px = ev.clientX - rect.left
    let best = 0; let bd = Infinity
    xs.forEach((x, i) => { const dd = Math.abs(X(x) - px); if (dd < bd) { bd = dd; best = i } })
    cross.setAttribute('x1', X(xs[best]))
    cross.setAttribute('x2', X(xs[best]))
    cross.setAttribute('opacity', 1)
    tipShow((p) => {
      tipTitle(p, `Week of ${fmtDate(weeks[best])}`)
      series.forEach((s) => {
        const y = s.points[best]
        tipRow(p, s.color, y === null ? '—' : vFmt(y), s.label)
      })
    }, ev)
  })
  hit.addEventListener('pointerleave', () => { cross.setAttribute('opacity', 0); tipHide() })
}

// ---------- Compare: horizontal stacked bars ----------
// rows: [{ label, total, parts: [{ label, color, value }] }]
export function hBarsStacked(host, rows, opts = {}) {
  host.innerHTML = ''
  if (!rows.length || !rows.some(r => r.total > 0)) { emptyMsg(host); return }
  const W = Math.max(host.clientWidth || 480, 300)
  const rowH = 34; const mT = 6
  const labelW = opts.labelW || 110; const valueW = 56; const mR = 10
  const svg = sv('svg', { width: W, height: mT + rows.length * rowH + 4 }, host)
  const innerW = W - labelW - valueW - mR
  const maxV = Math.max(...rows.map(r => r.total))
  rows.forEach((r, i) => {
    const yMid = mT + rowH * i + rowH / 2
    const lbl = sv('text', { x: labelW - 10, y: yMid + 4, 'text-anchor': 'end', 'font-size': 12, fill: 'var(--m-ink2)' }, svg)
    lbl.textContent = r.label
    let x = labelW
    const segs = r.parts.filter(p => p.value > 0)
    segs.forEach((p, si) => {
      const w = (p.value / maxV) * innerW
      const isLast = si === segs.length - 1
      const drawW = Math.max(w - (isLast ? 0 : 2), 0.75)
      if (isLast) sv('path', { d: roundedRightRect(x, yMid - 9, drawW, 18, 4), fill: p.color }, svg)
      else sv('rect', { x, y: yMid - 9, width: drawW, height: 18, fill: p.color }, svg)
      x += w
    })
    const val = sv('text', { x: x + 8, y: yMid + 4, 'font-size': 12, fill: 'var(--m-ink)', 'font-weight': 600 }, svg)
    val.textContent = fmtNum(r.total)
    const hit = sv('rect', { x: 0, y: mT + rowH * i, width: W, height: rowH, fill: 'transparent' }, svg)
    hit.addEventListener('pointermove', (ev) => {
      tipShow((p) => {
        tipTitle(p, r.label)
        r.parts.forEach((part) => { if (part.value > 0) tipRow(p, part.color, fmtNum(part.value), part.label) })
        tipRow(p, null, fmtNum(r.total), 'Total').classList.add('t-total')
      }, ev)
    })
    hit.addEventListener('pointerleave', tipHide)
  })
}

// ---------- Technician dashboard ----------

export function fmtMonth(iso) {
  return MONTHS[new Date(`${iso}T12:00:00`).getMonth()]
}

// Part-to-whole identity. rows: [{ label, value, color }] — the CALLER caps
// the series count and assigns colors in fixed slot order (never cycled);
// anything beyond the cap arrives pre-folded into an "Other" row.
export function donut(host, rows, opts = {}) {
  host.innerHTML = ''
  const data = rows.filter(r => r.value > 0)
  if (!data.length) { emptyMsg(host); return }
  const total = data.reduce((a, r) => a + r.value, 0)
  const size = opts.size || 168
  const wrap = div('m-donutwrap', host)
  const svg = sv('svg', { width: size, height: size, viewBox: `0 0 ${size} ${size}` }, wrap)
  const cx = size / 2
  const cy = size / 2
  const rOut = size / 2 - 3
  const rIn = rOut * 0.64
  let a = -Math.PI / 2
  data.forEach((r) => {
    // cap just under a full turn — an arc whose start equals its end draws nothing
    const a2 = a + Math.min(r.value / total, 0.99995) * Math.PI * 2
    const large = a2 - a > Math.PI ? 1 : 0
    const pt = (rad, ang) => `${cx + rad * Math.cos(ang)},${cy + rad * Math.sin(ang)}`
    const seg = sv('path', {
      d: `M${pt(rOut, a)} A${rOut},${rOut} 0 ${large} 1 ${pt(rOut, a2)} L${pt(rIn, a2)} A${rIn},${rIn} 0 ${large} 0 ${pt(rIn, a)} Z`,
      fill: r.color,
      stroke: 'var(--m-surface)',
      'stroke-width': 2 // the 2px surface gap between fills
    }, svg)
    seg.addEventListener('pointermove', (ev) => {
      tipShow((p) => {
        tipTitle(p, r.label)
        tipRow(p, r.color, `${fmtNum(r.value, opts.dp || 0)}${opts.unit || ''}`, `${fmtNum((r.value / total) * 100, 1)}%`)
      }, ev)
    })
    seg.addEventListener('pointerleave', tipHide)
    a = a2
  })
  const center = sv('text', { x: cx, y: cy - 1, 'text-anchor': 'middle', 'font-size': 17, 'font-weight': 700, fill: 'var(--m-ink)' }, svg)
  center.textContent = fmtNum(total, opts.dp || 0)
  if (opts.centerSub) {
    const sub = sv('text', { x: cx, y: cy + 15, 'text-anchor': 'middle', 'font-size': 11, fill: 'var(--m-muted)' }, svg)
    sub.textContent = opts.centerSub
  }
  const legend = div('m-donut-legend', wrap)
  data.forEach((r) => {
    const row = div('m-dl-row', legend)
    const sw = document.createElement('span')
    sw.className = 'm-swatch'
    sw.style.background = r.color
    row.appendChild(sw)
    const lbl = document.createElement('span')
    lbl.className = 'm-dl-label'
    lbl.textContent = r.label
    row.appendChild(lbl)
    const val = document.createElement('span')
    val.className = 'm-dl-value'
    val.textContent = `${fmtNum(r.value, opts.dp || 0)} (${fmtNum((r.value / total) * 100, 1)}%)`
    row.appendChild(val)
  })
}

// Utilization meter: pct 0-100 (may exceed 100; the arc clamps, the number
// doesn't). null pct = no capacity data to divide by.
export function gauge(host, pct, opts = {}) {
  host.innerHTML = ''
  if (pct === null || pct === undefined) { emptyMsg(host, opts.empty || 'No available-hours data.'); return }
  const W = Math.max(Math.min(host.clientWidth || 200, 240), 150)
  const stroke = 13
  const r = W / 2 - stroke
  const cx = W / 2
  const cy = r + stroke / 2 + 4
  const H = cy + 26
  const svg = sv('svg', { width: W, height: H, viewBox: `0 0 ${W} ${H}` }, host)
  const arc = (from, to) => {
    const p = ang => `${cx + r * Math.cos(ang)},${cy + r * Math.sin(ang)}`
    const large = to - from > Math.PI ? 1 : 0
    return `M${p(from)} A${r},${r} 0 ${large} 1 ${p(to)}`
  }
  sv('path', { d: arc(Math.PI, Math.PI * 2), stroke: 'var(--m-grid)', 'stroke-width': stroke, fill: 'none', 'stroke-linecap': 'round' }, svg)
  const frac = Math.max(0, Math.min(pct, 100)) / 100
  if (frac > 0) {
    sv('path', { d: arc(Math.PI, Math.PI + frac * Math.PI), stroke: opts.color || 'var(--m-s1)', 'stroke-width': stroke, fill: 'none', 'stroke-linecap': 'round' }, svg)
  }
  const num = sv('text', { x: cx, y: cy - 4, 'text-anchor': 'middle', 'font-size': 26, 'font-weight': 700, fill: 'var(--m-ink)' }, svg)
  num.textContent = `${fmtNum(pct)}%`
  if (opts.sub) {
    const sub = sv('text', { x: cx, y: cy + 14, 'text-anchor': 'middle', 'font-size': 11, fill: 'var(--m-muted)' }, svg)
    sub.textContent = opts.sub
  }
  const lo = sv('text', { x: cx - r, y: cy + 16, 'text-anchor': 'middle', 'font-size': 10, fill: 'var(--m-muted)' }, svg)
  lo.textContent = '0%'
  const hi = sv('text', { x: cx + r, y: cy + 16, 'text-anchor': 'middle', 'font-size': 10, fill: 'var(--m-muted)' }, svg)
  hi.textContent = '100%'
}

// Paired stacked columns with overlay reference lines, ONE shared y-scale
// (everything is hours). data: {
//   groups: [iso month starts],
//   clusters: [{ key, label, series: [{ label, color, values }], faded }],
//   lines: [{ label, values, color, dash }]
// }
export function pairedStacks(host, data, opts = {}) {
  host.innerHTML = ''
  const groups = data.groups || []
  const clusters = data.clusters || []
  const lines = data.lines || []
  const clusterTotals = clusters.map(c => groups.map((_, i) => c.series.reduce((a, s) => a + (s.values[i] || 0), 0)))
  const barMax = Math.max(0, ...clusterTotals.flat())
  const lineMax = Math.max(0, ...lines.flatMap(l => l.values))
  if (!groups.length || (barMax === 0 && lineMax === 0)) { emptyMsg(host); return }
  const W = Math.max(host.clientWidth || 640, 420)
  const plotH = opts.plotH || 250
  const mT = 10; const mB = 24; const mL = 48; const mR = 8
  const svg = sv('svg', { width: W, height: mT + plotH + mB }, host)
  const innerW = W - mL - mR
  const ticks = niceTicks(Math.max(barMax, lineMax), 4)
  const yMax = ticks[ticks.length - 1]
  drawGrid(svg, mL, mT, innerW, plotH, ticks, yMax)
  const band = innerW / groups.length
  const barW = Math.min(17, (band - 10) / Math.max(clusters.length, 1) - 3)
  const yPix = v => mT + plotH - (v / yMax) * plotH
  const clusterX = (gi, ci) => {
    const mid = mL + band * gi + band / 2
    const span = clusters.length * barW + (clusters.length - 1) * 3
    return mid - span / 2 + ci * (barW + 3)
  }
  const labelEvery = Math.max(1, Math.ceil(groups.length / Math.floor(innerW / 44)))

  groups.forEach((month, gi) => {
    clusters.forEach((cluster, ci) => {
      const x = clusterX(gi, ci)
      const g = sv('g', {}, svg)
      if (cluster.faded) g.setAttribute('opacity', 0.55)
      const segs = cluster.series.filter(s => (s.values[gi] || 0) > 0)
      let cum = 0
      segs.forEach((s, si) => {
        const v = s.values[gi]
        const yTop = yPix(cum + v)
        const yBot = yPix(cum)
        const isTop = si === segs.length - 1
        const gapTop = isTop ? 0 : 2 // 2px surface gap between stacked segments
        const h = Math.max(yBot - yTop - gapTop, 0.75)
        if (isTop) sv('path', { d: roundedTopRect(x, yTop, barW, h, 4), fill: s.color }, g)
        else sv('rect', { x, y: yTop + gapTop, width: barW, height: h, fill: s.color }, g)
        cum += v
      })
      const hit = sv('rect', { x: x - 1.5, y: mT, width: barW + 3, height: plotH, fill: 'transparent' }, svg)
      hit.addEventListener('pointerenter', () => { g.style.filter = 'brightness(1.08)' })
      hit.addEventListener('pointerleave', () => { g.style.filter = ''; tipHide() })
      hit.addEventListener('pointermove', (ev) => {
        tipShow((p) => {
          tipTitle(p, `${fmtMonth(month)} · ${cluster.label}`);
          [...cluster.series].reverse().forEach((s) => {
            if ((s.values[gi] || 0) > 0) tipRow(p, s.color, fmtNum(s.values[gi]), s.label)
          })
          tipRow(p, null, fmtNum(clusterTotals[ci][gi]), 'Total').classList.add('t-total')
          lines.forEach((l) => {
            if (l.values[gi] !== undefined) tipRow(p, l.color, fmtNum(l.values[gi]), l.label)
          })
        }, ev)
      })
    })
    if (gi % labelEvery === 0) {
      const t = sv('text', { x: mL + band * gi + band / 2, y: mT + plotH + 16, 'text-anchor': 'middle', 'font-size': 11, fill: 'var(--m-muted)' }, svg)
      t.textContent = fmtMonth(month)
    }
  })

  lines.forEach((l) => {
    const y = gi => yPix(Math.min(l.values[gi] || 0, yMax))
    const stroke = {
      fill: 'none',
      stroke: l.color,
      'stroke-width': 2,
      'stroke-dasharray': l.dash ? '5 4' : 'none',
      'stroke-linejoin': 'round',
      'stroke-linecap': 'round'
    }
    if (groups.length === 1) {
      // One group is one point, which a polyline can't show: draw the level
      // as a segment spanning the cluster so the line survives a single month.
      const mid = mL + band / 2
      const half = (clusters.length * barW + (clusters.length - 1) * 3) / 2 + 8
      sv('line', { x1: mid - half, x2: mid + half, y1: y(0), y2: y(0), ...stroke }, svg)
      return
    }
    const pts = groups.map((_, gi) => `${mL + band * gi + band / 2},${y(gi)}`).join(' ')
    sv('polyline', { points: pts, ...stroke }, svg)
  })
}
